import {RenderRule, ResourceType} from "./types";
import {isValidFileType} from "./contentUtils";
import {Image} from "./components/image/Image";
import {AutoLinkNone} from "./components/autolink/AutoLinkNone";
import {AutoLink} from "./components/autolink/AutoLink";
import {File} from "./components/file/file";
import {PanelLink} from "./components/panel-link/PanelLink";
import {ExmarkLink} from "./components/exmark-link/ExmarkLink";
import {AutoLinkNav} from "./components/autolink-nav/AutoLinkNav";
import {JumpLink} from "./components/jump-link/JumpLink";
import {Xbox} from "./components/xbox/Xbox";
import {Table} from "./components/table/Table";
import {Booktags} from "./components/booktags/Booktags";
import {XFootnote} from "./components/x-footnote/XFootnote";
import {ExternalLink} from "./components/external-link/ExternalLink";
import pageBreakClasses from "./components/page-break/pageBreak.module.css";

/**
 * Slots in the ruleset whose rendered component differs between the
 * canonical and LEGACY views. Empty for now — step 4 of the parent
 * plan (`.local/2026-05-21/standard-topic-styling/PLAN.md`) populates
 * this as `XboxLEGACY`, `TableLEGACY`, etc. land.
 *
 * Components listed here are looked up when the ruleset is built; an
 * entry left `undefined` falls back to the canonical import.
 */
export type LegacyOverrides = {
    Xbox?: typeof Xbox;
    Table?: typeof Table;
    ExmarkLink?: typeof ExmarkLink;
    ExternalLink?: typeof ExternalLink;
    Booktags?: typeof Booktags;
};

export const buildRuleset = (overrides: LegacyOverrides = {}): RenderRule[] => {
    const XboxC = overrides.Xbox ?? Xbox;
    const TableC = overrides.Table ?? Table;
    const ExmarkLinkC = overrides.ExmarkLink ?? ExmarkLink;
    const ExternalLinkC = overrides.ExternalLink ?? ExternalLink;
    const BooktagsC = overrides.Booktags ?? Booktags;

    return [
        // Image
        // auto-link-none
        // Kind: auto-link, object.type: linkdata, og har children -> AutoLink
        // Kind: preview, object.type: linkdata, og har children -> PreviewLink (hva er forskjellen?)
        // Kind: nav, object.type: linkdata, isValidFileType -> FileRef
        // Kind: "ilink nav" || "nav", object.type: linkdata, children.length > 0 -> AutoLinkNav
        // Kind: "auto-link-nav" || "nav", object.type: linkdata, children.length > 0 -> AutoLinkNav
        //       Konkat disse to.
        // Kind: "ilink definition" || (kind: "ilink" && node.element.attributes.class: "ilink definition") || node.element.attributes.class: "ilink" -> IfrsDefLink
        // Kind: "ilink nav", element.attributes["data-segment"] !== undefined, element.attributes["data-id"] !== undefined -> JumpLink
        // Kind: ("ilink footnote" || "ilink footnote edu"), element.attributes["data-segment"] !== undefined, element.attributes["data-id"] !== undefined -> IfrsFootnote
        // Kind: "ilink preview exmark", element.attributes["data-id"] !== undefined -> ExmarkLink
        // Kind: "href", element.attributes.class: "href", children.length > 0, object.data.href.length > 0 -> ExternalHref
        // Kind: "xbox" || "xbox open" -> Xbox
        // Element.name: "table", topicData.resourceTypeId !== documentgenerator && topicData.resourceTypeId !== checklist -> Table
        // Kind: booktags -> Booktags

        // Dropp denne, men HUSK HVORFOR VI HAR DEN. Den brukes kun til å rendre "related" i LEGACY sjekklister:
        // Kind: related, options.sliderRelation -> RelatedLink
        // Dropp denne, men HUSK HVORFOR VI HAR DEN  Den brukes kun til å rendre "related" i LEGACY sjekklister:
        // Kind: related, object.data.topicId === undefined OG object.data.categoryId er enten INDEX, SOURCES eller TOOLS -> RelatedLink

        // Kind: dib-x-footnote, element.attributes["data-title"] !== undefined, element.attributes.id !== undefined -> XFootnte
        // Kind: dib-x-pagebreak -> PageBreak

        // Total:
        // - Image
        // - AutoLinkNone
        // - AutoLink
        // - PreviewLink
        // - FileRef
        // - AutoLinkNav
        // - IfrsDefLink
        // - IfrsFootnote
        // - JumpLink
        // - ExmarkLink
        // - ExternalHref
        // - Xbox
        // - Table
        // - Booktags
        // - XFootnote
        // - PageBreak
        {
            shouldParse: (node) => node.kind === "image",
            parser: (node) => (
                <Image node={node} key={node.element?.attributes?.id} />
            ),
        },
        {
            shouldParse: (node) => node.kind === "auto-link-none",
            parser: (node, options, data) => (
                <AutoLinkNone
                    children={data?.children ?? []}
                    key={node.element?.attributes?.id}
                />
            ),
        },
        {
            // Auto-link
            shouldParse: (node) => {
                return !!(
                    node.kind === "auto-link" &&
                    node.object?.type === "linkdata" &&
                    node?.element?.children?.length &&
                    node?.element?.children?.length > 0
                );
            },
            parser: (node, options, data) => {
                return (
                    <AutoLink
                        rootTopicId={options.rootTopicId}
                        children={data?.children ?? []}
                        map={data!.map!}
                        node={node}
                        key={node.element?.attributes?.id}
                    />
                );
            },
        },
        {
            // Preview link
            shouldParse: (node) => {
                return !!(
                    node.kind === "preview" &&
                    node.object?.type === "linkdata" &&
                    node?.element?.children?.length &&
                    node?.element?.children?.length > 0
                );
            },
            parser: (node, options, data) => {
                return (
                    <AutoLink
                        children={data?.children ?? []}
                        map={data!.map!}
                        node={node}
                        key={node.element?.attributes?.id}
                    />
                );
            },
        },
        {
            // File reference
            shouldParse: (node) => {
                return !!(
                    (node.kind === "nav" || node.kind === "auto-link-nav") &&
                    node.object?.type === "linkdata" &&
                    isValidFileType(
                        node.object.data
                            .resourceTypeId as unknown as ResourceType
                    )
                );
            },
            parser: (node, options, data) => {
                return (
                    <File
                        children={data?.children ?? []}
                        node={node}
                        key={node.element?.attributes?.id}
                    />
                );
            },
        },
        {
            // AutoLinkNav – "ilink nav" | "nav" | "auto-link-nav" with linkdata children
            shouldParse: (node) =>
                !!(
                    (node.kind === "ilink nav" ||
                        node.kind === "nav" ||
                        node.kind === "auto-link-nav") &&
                    node.object?.type === "linkdata" &&
                    node.element?.children?.length &&
                    node.element.children.length > 0
                ),
            parser: (node, _options, data) => (
                <AutoLinkNav
                    node={node}
                    children={data?.children ?? []}
                    key={node.element?.attributes?.id}
                />
            ),
        },
        {
            // JumpLink – same-topic "ilink nav" (no linkdata); AutoLinkNav captures cross-topic variants first
            shouldParse: (node) =>
                !!(
                    node.kind === "ilink nav" &&
                    node.element?.attributes?.["data-segment"] !== undefined &&
                    node.element?.attributes?.["data-id"] !== undefined
                ),
            parser: (node, _options, data) => (
                <JumpLink
                    node={node}
                    map={data!.map!}
                    children={data?.children ?? []}
                    key={node.element?.attributes?.id}
                />
            ),
        },
        {
            // IfrsDefLink
            shouldParse: (node) =>
                !!(
                    node.kind === "ilink definition" ||
                    (node.kind === "ilink" &&
                        node.element?.attributes?.class ===
                            "ilink definition") ||
                    node.element?.attributes?.class === "ilink"
                ),
            parser: (node, _options, data) => (
                <PanelLink
                    node={node}
                    map={data!.map!}
                    children={data?.children ?? []}
                    variant="definition"
                    key={node.element?.attributes?.id}
                />
            ),
        },
        {
            // IfrsFootnote – "ilink footnote" | "ilink footnote edu" with segment+id
            shouldParse: (node) =>
                !!(
                    (node.kind === "ilink footnote" ||
                        node.kind === "ilink footnote edu") &&
                    node.element?.attributes?.["data-segment"] !== undefined &&
                    node.element?.attributes?.["data-id"] !== undefined
                ),
            parser: (node, _options, data) => (
                <PanelLink
                    node={node}
                    map={data!.map!}
                    children={data?.children ?? []}
                    variant={
                        node.kind === "ilink footnote edu"
                            ? "footnote-edu"
                            : "footnote"
                    }
                    key={node.element?.attributes?.id}
                />
            ),
        },
        {
            // ExmarkLink
            shouldParse: (node) =>
                !!(
                    node.kind === "ilink preview exmark" &&
                    node.element?.attributes?.["data-id"] !== undefined
                ),
            parser: (node, _options, data) => (
                <ExmarkLinkC
                    node={node}
                    map={data!.map!}
                    key={node.element?.attributes?.id}
                />
            ),
        },
        {
            // ExternalHref
            shouldParse: (node) =>
                !!(
                    node.kind === "href" &&
                    node.element?.attributes?.class === "href" &&
                    node.element?.children?.length &&
                    node.element.children.length > 0 &&
                    node.object?.type === "linkdata" &&
                    node.object.data.href &&
                    node.object.data.href.length > 0
                ),
            parser: (node, _options, data, key) => (
                <ExternalLinkC node={node} key={key}>
                    {data?.children ?? []}
                </ExternalLinkC>
            ),
        },
        {
            // External links with no linkdata
            shouldParse: (node) => {
                if (
                    !node.kind &&
                    node.element?.name === "a" &&
                    node.element.attributes?.href?.startsWith("http")
                ) {
                    return true;
                }
                return false;
            },
            parser: (node, _options, data, key) => (
                <ExternalLinkC node={node} key={key}>
                    {data?.children ?? []}
                </ExternalLinkC>
            ),
        },
        {
            // Xbox
            shouldParse: (node) =>
                node.kind === "xbox" || node.kind === "xbox open",
            parser: (node, _options, data, key) => (
                <XboxC
                    node={node}
                    map={data!.map!}
                    children={data?.children ?? []}
                    key={key}
                />
            ),
        },
        {
            // Table – not documentGenerator, not checklist, not byggekloss
            shouldParse: (node, map) =>
                !!(
                    node.element?.name === "table" &&
                    map.resourceTypeId !== ResourceType.documentGenerator &&
                    map.resourceTypeId !== ResourceType.checklist &&
                    map.resourceTypeId !== ResourceType.byggekloss
                ),
            parser: (node, _options, data, key) => (
                <TableC node={node} children={data?.children ?? []} key={key} />
            ),
        },
        {
            // Booktags
            shouldParse: (node) => node.kind === "booktags",
            parser: (node, _options, data, key) => (
                <BooktagsC
                    node={node}
                    children={data?.children ?? []}
                    key={key}
                />
            ),
        },
        {
            // XFootnote
            shouldParse: (node) =>
                !!(
                    node.kind === "dib-x-footnote" &&
                    (node.element?.attributes?.["data-title"] ??
                        node.element?.attributes?.["title"]) !== undefined &&
                    node.element?.attributes?.id !== undefined
                ),
            parser: (node, _options, _data, key) => (
                <XFootnote node={node} key={key} />
            ),
        },
        {
            // PageBreak
            shouldParse: (node) => node.kind === "dib-x-pagebreak",
            parser: (_node, _options, _data, key) => (
                <div className={pageBreakClasses.pageBreak} key={key} />
            ),
        },

        // This always go last:
        {
            shouldParse: (_: any) => true,
            parser: (_: any) => null,
        },
    ];
};

/**
 * Canonical ruleset (no LEGACY overrides). Kept as a named export for
 * back-compat with `renderNode.ts` and `DocgenChecklistView.tsx`.
 */
export const ruleset: RenderRule[] = buildRuleset();
